/**
 * 🚀 VOICE ACTOR WORKSHEET DATA UTILITIES SLICE
 * 
 * Specialized utilities for file uploads, media processing, and external integrations
 * Adapted from bookWorksheet utilities for VoiceActor entity hierarchy
 * 
 * Responsibilities:
 * - Voice snippet uploads (audio files)
 * - Text snippet uploads (text files)
 * - Speech sample record creation
 * - Media processing utilities
 * 
 * Entity Mapping:
 * BookWorksheet → VoiceActor
 * litGroupId   → actorId
 * bookId       → portfolioId
 * characterId  → characterId (same)
 * variationId  → variationId (same)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

// Import selectors from main data slice for cross-slice communication
const selectVariationZone = (state) => state.user.voiceActorWorksheetData?.variationZone;
const selectSharedDataSelections = (state) => state.user.voiceActorWorksheetData?.sharedData?.selections;
const selectUserProfile = (state) => state.user.profile;

// ===== UTILITY FUNCTIONS =====

/**
 * Calculate the next version number for a variation
 */
const calculateNextVersion = (variationId, getState) => {
  const state = getState();
  const variationZone = selectVariationZone(state);
  const variation = variationZone?.variations?.find(v => v.uuid === variationId);
  return (variation?.version || 1) + 1;
};

/**
 * Extract metadata from current Redux state
 */
const extractMetadataFromState = (getState) => {
  const state = getState();
  const selections = selectSharedDataSelections(state);
  const characterZone = state.user.voiceActorWorksheetData?.characterZone;

  return {
    actorId: selections?.actorId,
    portfolioId: selections?.portfolioId,
    characterId: selections?.characterId,
    characterName: characterZone?.selectedCharacter?.name || 'Unknown Character'
  };
};

// ===== ASYNC THUNKS =====

/**
 * Upload voice snippet (audio file) to S3
 */
export const uploadVoiceSnippet = createAsyncThunk(
  'vaUtilities/uploadVoiceSnippet',
  async ({
    audioBlob,
    variationId,
    speechSampleId, // Optional - generate if not provided
    version, // Optional - calculate if not provided
    metadata: {
      actorId,
      portfolioId,
      characterId,
      variationName,
      fileDuration
    },
    uploadMedia // Receive uploadMedia function as parameter
  }, { getState, rejectWithValue }) => {
    try {
      // ✅ ENHANCED: Validate real variationId before upload
      if (!variationId || variationId === null || variationId === 'null') {
        console.error('[uploadVoiceSnippet] Invalid variationId provided:', variationId);
        throw new Error('Real variationId is required for voice upload. Variation must be created first.');
      }

      console.log('[uploadVoiceSnippet] Starting voice upload:', {
        variationId,
        speechSampleId,
        version,
        blobSize: audioBlob?.size
      });

      // 1. Generate IDs if not provided
      const finalSpeechSampleId = speechSampleId || uuidv4();
      const finalVersion = version || calculateNextVersion(variationId, getState);

      // 2. Create filename
      const fileName = `${variationName.replace(/[^\w\s-]/g, '')}_V${finalVersion}.wav`;

      // 3. Create File object
      const file = new File([audioBlob], fileName, { type: 'audio/wav' });

      // 4. Get userId from state
      const state = getState();
      const userProfile = selectUserProfile(state);
      const userId = userProfile?.data?.userId;

      if (!userId) {
        throw new Error('User ID not found in state');
      }

      // 5. Use the uploadMedia function passed as parameter
      if (!uploadMedia) {
        throw new Error('uploadMedia function not provided');
      }

      // 6. Upload to S3 using VoiceActor path registry
      const voiceS3Url = await uploadMedia(
        file,
        'voiceActorVariationSpeechSampleVoice',
        {
          actorId,
          portfolioId,
          characterId,
          variationId,
          speechSampleId: finalSpeechSampleId,
          fileName,
          ...(fileDuration && { fileDuration })
        },
        userId
      );

      console.log('[uploadVoiceSnippet] Voice upload completed:', voiceS3Url);

      return {
        voiceS3Url,
        speechSampleId: finalSpeechSampleId,
        fileName,
        version: finalVersion,
        fileDuration,
        variationId
      };
    } catch (error) {
      console.error('[uploadVoiceSnippet] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Upload text snippet (text file) to S3
 */
export const uploadTextSnippet = createAsyncThunk(
  'vaUtilities/uploadTextSnippet',
  async ({
    textContent,
    variationId,
    speechSampleId, // Optional - generate if not provided
    version, // Optional - calculate if not provided
    metadata: {
      actorId,
      portfolioId,
      characterId,
      variationName
    },
    uploadMedia // Receive uploadMedia function as parameter
  }, { getState, rejectWithValue }) => {
    try {
      console.log('[uploadTextSnippet] Starting text upload:', {
        variationId,
        speechSampleId,
        version,
        contentLength: textContent?.length
      });

      // 1. Generate IDs if not provided
      const finalSpeechSampleId = speechSampleId || uuidv4();
      const finalVersion = version || calculateNextVersion(variationId, getState);

      // 2. Create filename
      const fileName = `${variationName.replace(/[^\w\s-]/g, '')}_V${finalVersion}.txt`;

      // 3. Create File object
      const textFile = new File([textContent], fileName, { type: 'text/plain' });

      // 4. Get userId from state
      const state = getState();
      const userProfile = selectUserProfile(state);
      const userId = userProfile?.data?.userId;

      if (!userId) {
        throw new Error('User ID not found in state');
      }

      // 5. Use the uploadMedia function passed as parameter
      if (!uploadMedia) {
        throw new Error('uploadMedia function not provided');
      }

      // 6. Upload to S3 using VoiceActor path registry
      const textS3Url = await uploadMedia(
        textFile,
        'voiceActorVariationSpeechSampleText',
        {
          actorId,
          portfolioId,
          characterId,
          variationId,
          speechSampleId: finalSpeechSampleId,
          fileName
        },
        userId
      );

      console.log('[uploadTextSnippet] Text upload completed:', textS3Url);

      return {
        textS3Url,
        speechSampleId: finalSpeechSampleId,
        fileName,
        version: finalVersion,
        textContent,
        variationId
      };
    } catch (error) {
      console.error('[uploadTextSnippet] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Create speech sample record in database
 */
export const createSpeechSampleRecord = createAsyncThunk(
  'vaUtilities/createSpeechSampleRecord',
  async ({
    speechSampleData: {
      speechSampleId,
      variationId,
      variationName,
      version,
      voiceS3Url,
      textS3Url,
      textContent,
      fileDuration,
      metadata
    }
  }, { getState, rejectWithValue }) => {
    try {
      console.log('[createSpeechSampleRecord] Creating database record:', {
        speechSampleId,
        variationId,
        version
      });

      const state = getState();
      const userProfile = selectUserProfile(state);
      const userId = userProfile?.data?.userId;

      if (!userId) {
        throw new Error('User ID not found in state');
      }

      // Dynamically import API dependencies
      const { apiClient } = await import('../../apiTools/apiClient');
      const { config } = await import('../../constants/config');

      // Extract properties from metadata
      const { actorId, portfolioId, characterId, characterName } = metadata || {};

      const charSpeechSampleParams = {
        userId,
        actorId: actorId,
        portfolioId: portfolioId,
        characterId: characterId,
        characterName: metadata?.characterName || characterName || null,
        characterDescription: metadata?.characterDescription || null,
        characterAvatarUrl: metadata?.characterAvatarUrl || null,
        variationId,
        variationName,
        speechSampleId,
        textSnippetUuid: speechSampleId,
        voiceSnippetUuid: speechSampleId,
        title: `${characterName} - ${variationName}`,
        revision: version.toString(),
        isSelected: true,
        enabled: true,
        textFileName: textS3Url ? textS3Url.split('/').pop() : null,
        voiceFileName: voiceS3Url ? voiceS3Url.split('/').pop() : null,
        textS3Url: textS3Url || null,
        voiceS3Url: voiceS3Url || null,
        content: textContent || '',
        fileDuration: fileDuration || null
      };

      // Check if this is a Sample Character (no actorId or actorId is the same as userId typically means it's a sample)
      // Also, we can check in the Redux state if this is being called from SampleCharacterForm context
      const isSampleCharacter = metadata?.isSampleCharacter || !actorId || actorId === userId;

      // Use appropriate endpoint based on character type
      // For sample characters, use the hardcoded endpoint since it's not in config yet
      // Note: apiClient already prepends /api, so we don't include it here
      const endpoint = isSampleCharacter
        ? config.ADD_VA_CHARACTER_SPEECH_SAMPLE_V3
        : config.ADD_VA_CHARACTER_SPEECH_SAMPLE_V2;

      console.log('[createSpeechSampleRecord] Using endpoint:', endpoint, 'isSampleCharacter:', isSampleCharacter);

      const response = await apiClient.post(endpoint, charSpeechSampleParams);

      console.log('[createSpeechSampleRecord] Database record created successfully');

      return {
        speechSample: response.data,
        speechSampleId,
        variationId
      };
    } catch (error) {
      console.error('[createSpeechSampleRecord] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update text snippet only for existing version
 */
export const updateTextSnippetOnly = createAsyncThunk(
  'vaUtilities/updateTextSnippetOnly',
  async ({
    versionId, // Existing version/sample ID
    newTextContent,
    variationId,
    metadata
  }, { dispatch, getState, rejectWithValue }) => {
    try {
      console.log('[updateTextSnippetOnly] Updating text for existing version:', {
        versionId,
        variationId,
        contentLength: newTextContent?.length
      });

      // 1. Upload new text file
      const textResult = await dispatch(uploadTextSnippet({
        textContent: newTextContent,
        variationId,
        speechSampleId: versionId, // Use existing version ID
        metadata
      }));

      if (textResult.type.endsWith('/rejected')) {
        throw new Error(textResult.payload);
      }

      // 2. Update the existing speech sample record
      const state = getState();
      const userProfile = selectUserProfile(state);
      const userId = userProfile?.data?.userId;

      if (!userId) {
        throw new Error('User ID not found in state');
      }

      const { apiClient } = await import('../../apiTools/apiClient');
      const { config } = await import('../../constants/config');

      // Update the existing record with new text URL and content
      await apiClient.put(config.UPDATE_VA_CHARACTER_SPEECH_SAMPLE || config.PUT_API_VA_DIRECT_VARIATION, {
        speechSampleId: versionId,
        textS3Url: textResult.payload.textS3Url,
        content: newTextContent,
        textFileName: textResult.payload.fileName
      });

      console.log('[updateTextSnippetOnly] Text snippet updated successfully');

      return {
        versionId,
        textS3Url: textResult.payload.textS3Url,
        textContent: newTextContent,
        variationId
      };
    } catch (error) {
      console.error('[updateTextSnippetOnly] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Update voice snippet database record after S3 upload
 */
export const updateVoiceSnippetDb = createAsyncThunk(
  'vaUtilities/updateVoiceSnippetDb',
  async ({ portfoliouuid, sampleuuid, s3Url, fileDuration }, { rejectWithValue }) => {
    try {
      console.log('[updateVoiceSnippetDb] Updating voice snippet in database:', {
        portfoliouuid,
        sampleuuid,
        s3Url: s3Url?.substring(0, 50) + '...',
        fileDuration
      });

      // Input validation
      if (!portfoliouuid || !sampleuuid) {
        return rejectWithValue('portfoliouuid and sampleuuid are required parameters');
      }

      if (!s3Url && fileDuration === undefined) {
        return rejectWithValue('At least one property (s3Url or fileDuration) must be provided for update');
      }

      // Build query parameters
      const queryParams = { portfoliouuid, sampleuuid };
      const updatedFields = [];

      if (s3Url) {
        queryParams.s3Url = s3Url;
        updatedFields.push('s3Url');
      }

      if (fileDuration !== undefined) {
        queryParams.fileDuration = fileDuration;
        updatedFields.push('fileDuration');
      }

      // Dynamically import API dependencies
      const { apiClient } = await import('../../apiTools/apiClient');
      const { config } = await import('../../constants/config');

      // Make API call with correct query parameter syntax
      const response = await apiClient.put(
        config.UPDATE_VA_VOICE_SNIPPET || `${config.BASE_URL}/update-va-voice-snippet`,
        {}, // Empty body
        {}, // No URL params to substitute  
        { params: queryParams } // Query parameters in config
      );

      console.log('[updateVoiceSnippetDb] Voice snippet updated successfully in database');

      return {
        portfoliouuid,
        sampleuuid,
        updatedFields,
        charSpeechSample: response.data.charSpeechSample,
        voiceSnippet: response.data.voiceSnippet,
        s3Url: s3Url || null,
        fileDuration: fileDuration || null,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('[updateVoiceSnippetDb] Error updating voice snippet:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Upload complete version (both audio and text) - main upload function
 */
export const uploadCompleteVersion = createAsyncThunk(
  'vaUtilities/uploadCompleteVersion',
  async ({ audioBlob, textContent, variationId, metadata }, { dispatch, getState, rejectWithValue }) => {
    try {
      console.log('[uploadCompleteVersion] Starting complete version upload:', {
        variationId,
        hasAudio: !!audioBlob,
        hasText: !!textContent
      });

      const speechSampleId = uuidv4();
      const results = {};

      // Get metadata from state if not provided
      const completeMetadata = {
        ...extractMetadataFromState(getState),
        ...metadata
      };

      // Upload voice if provided
      if (audioBlob) {
        const voiceResult = await dispatch(uploadVoiceSnippet({
          audioBlob,
          variationId,
          speechSampleId,
          metadata: completeMetadata
        }));

        if (voiceResult.type.endsWith('/rejected')) {
          throw new Error(`Voice upload failed: ${voiceResult.payload}`);
        }

        results.voice = voiceResult.payload;
      }

      // Upload text if provided
      if (textContent) {
        const textResult = await dispatch(uploadTextSnippet({
          textContent,
          variationId,
          speechSampleId,
          version: results.voice?.version, // Use same version if voice was uploaded
          metadata: completeMetadata
        }));

        if (textResult.type.endsWith('/rejected')) {
          throw new Error(`Text upload failed: ${textResult.payload}`);
        }

        results.text = textResult.payload;
      }

      // Create database record if we have either voice or text
      if (results.voice || results.text) {
        const recordResult = await dispatch(createSpeechSampleRecord({
          speechSampleData: {
            speechSampleId,
            variationId,
            variationName: metadata.variationName,
            version: results.voice?.version || results.text?.version,
            voiceS3Url: results.voice?.voiceS3Url,
            textS3Url: results.text?.textS3Url,
            textContent,
            fileDuration: results.voice?.fileDuration,
            metadata: completeMetadata
          }
        }));

        if (recordResult.type.endsWith('/rejected')) {
          throw new Error(`Database record creation failed: ${recordResult.payload}`);
        }

        results.record = recordResult.payload;
      }

      console.log('[uploadCompleteVersion] Complete version upload successful');

      return results;
    } catch (error) {
      console.error('[uploadCompleteVersion] Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// ===== INITIAL STATE =====
const initialState = {
  uploadOperations: {
    voice: {
      loading: false,
      error: null,
      progress: 0,
      lastUpload: null
    },
    text: {
      loading: false,
      error: null,
      progress: 0,
      lastUpload: null
    },
    record: {
      loading: false,
      error: null,
      lastCreated: null
    },
    complete: {
      loading: false,
      error: null,
      lastUpload: null
    }
  }
};

// ===== SLICE DEFINITION =====
const vaUtilitiesSlice = createSlice({
  name: 'vaUtilities',
  initialState,
  reducers: {
    clearUploadErrors: (state) => {
      state.uploadOperations.voice.error = null;
      state.uploadOperations.text.error = null;
      state.uploadOperations.record.error = null;
      state.uploadOperations.complete.error = null;
    },

    clearAllUploadState: (state) => {
      state.uploadOperations = initialState.uploadOperations;
    },

    setUploadProgress: (state, action) => {
      const { operation, progress } = action.payload;
      if (state.uploadOperations[operation]) {
        state.uploadOperations[operation].progress = progress;
      }
    }
  },

  extraReducers: (builder) => {
    builder
      // ===== VOICE UPLOAD =====
      .addCase(uploadVoiceSnippet.pending, (state) => {
        state.uploadOperations.voice.loading = true;
        state.uploadOperations.voice.error = null;
        state.uploadOperations.voice.progress = 0;
      })
      .addCase(uploadVoiceSnippet.fulfilled, (state, action) => {
        state.uploadOperations.voice.loading = false;
        state.uploadOperations.voice.progress = 100;
        state.uploadOperations.voice.lastUpload = action.payload;
      })
      .addCase(uploadVoiceSnippet.rejected, (state, action) => {
        state.uploadOperations.voice.loading = false;
        state.uploadOperations.voice.error = action.payload;
        state.uploadOperations.voice.progress = 0;
      })

      // ===== TEXT UPLOAD =====
      .addCase(uploadTextSnippet.pending, (state) => {
        state.uploadOperations.text.loading = true;
        state.uploadOperations.text.error = null;
        state.uploadOperations.text.progress = 0;
      })
      .addCase(uploadTextSnippet.fulfilled, (state, action) => {
        state.uploadOperations.text.loading = false;
        state.uploadOperations.text.progress = 100;
        state.uploadOperations.text.lastUpload = action.payload;
      })
      .addCase(uploadTextSnippet.rejected, (state, action) => {
        state.uploadOperations.text.loading = false;
        state.uploadOperations.text.error = action.payload;
        state.uploadOperations.text.progress = 0;
      })

      // ===== RECORD CREATION =====
      .addCase(createSpeechSampleRecord.pending, (state) => {
        state.uploadOperations.record.loading = true;
        state.uploadOperations.record.error = null;
      })
      .addCase(createSpeechSampleRecord.fulfilled, (state, action) => {
        state.uploadOperations.record.loading = false;
        state.uploadOperations.record.lastCreated = action.payload;
      })
      .addCase(createSpeechSampleRecord.rejected, (state, action) => {
        state.uploadOperations.record.loading = false;
        state.uploadOperations.record.error = action.payload;
      })

      // ===== TEXT UPDATE ONLY =====
      .addCase(updateTextSnippetOnly.pending, (state) => {
        state.uploadOperations.text.loading = true;
        state.uploadOperations.text.error = null;
      })
      .addCase(updateTextSnippetOnly.fulfilled, (state, action) => {
        state.uploadOperations.text.loading = false;
        state.uploadOperations.text.lastUpload = action.payload;
      })
      .addCase(updateTextSnippetOnly.rejected, (state, action) => {
        state.uploadOperations.text.loading = false;
        state.uploadOperations.text.error = action.payload;
      })

      // ===== COMPLETE VERSION UPLOAD =====
      .addCase(uploadCompleteVersion.pending, (state) => {
        state.uploadOperations.complete.loading = true;
        state.uploadOperations.complete.error = null;
        // Also set individual operation loading states
        state.uploadOperations.voice.loading = true;
        state.uploadOperations.text.loading = true;
        state.uploadOperations.record.loading = true;
      })
      .addCase(uploadCompleteVersion.fulfilled, (state, action) => {
        state.uploadOperations.complete.loading = false;
        state.uploadOperations.complete.lastUpload = action.payload;
        // Clear individual loading states
        state.uploadOperations.voice.loading = false;
        state.uploadOperations.text.loading = false;
        state.uploadOperations.record.loading = false;
      })
      .addCase(uploadCompleteVersion.rejected, (state, action) => {
        state.uploadOperations.complete.loading = false;
        state.uploadOperations.complete.error = action.payload;
        // Clear individual loading states
        state.uploadOperations.voice.loading = false;
        state.uploadOperations.text.loading = false;
        state.uploadOperations.record.loading = false;
      });
  }
});

// Export actions
export const {
  clearUploadErrors,
  clearAllUploadState,
  setUploadProgress
} = vaUtilitiesSlice.actions;

// Export selectors
export const selectUploadOperations = (state) => state.user.vaUtilities?.uploadOperations;
export const selectVoiceUploadState = (state) => state.user.vaUtilities?.uploadOperations?.voice;
export const selectTextUploadState = (state) => state.user.vaUtilities?.uploadOperations?.text;
export const selectRecordCreationState = (state) => state.user.vaUtilities?.uploadOperations?.record;
export const selectCompleteUploadState = (state) => state.user.vaUtilities?.uploadOperations?.complete;

// Export reducer
export default vaUtilitiesSlice.reducer;
